// src/utils/exportUtils.js
import * as XLSX from 'xlsx';
import moment from 'moment';
import dayjs from 'dayjs';

const LEAVE_TYPE_LABELS = {
  'SICK LEAVE': 'Sick Leave',
  'LOCAL LEAVE': 'Local Leave',
  PH: 'Public Holiday',
  ABSENT: 'Absent',
  OFF: 'Day Off'
};

export const exportToExcel = (data, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportLeaveTypeReport = ({
  rows = [],
  records = [],
  summary = {},
  dateRange = [],
  companyName = 'Company',
  leaveTypeFilter = 'all',
  employeeName = 'All Employees'
}) => {
  const workbook = XLSX.utils.book_new();
  const startDate = dateRange?.[0] ? moment(dateRange[0]).format('DD/MM/YYYY') : '-';
  const endDate = dateRange?.[1] ? moment(dateRange[1]).format('DD/MM/YYYY') : '-';
  const filterLabel = leaveTypeFilter === 'all'
    ? 'All Leave Types'
    : (LEAVE_TYPE_LABELS[leaveTypeFilter] || leaveTypeFilter);

  const overviewData = [
    ['Leave & Attendance Report'],
    ['Company', companyName],
    ['Period', `${startDate} to ${endDate}`],
    ['Generated On', moment().format('DD/MM/YYYY HH:mm')],
    ['Employee Filter', employeeName],
    ['Leave Type Filter', filterLabel],
    [],
    ['Metric', 'Value'],
    ['Employees with Leave', summary.employeesWithLeave || 0],
    ['Total Leave Records', summary.totalLeaveRecords || 0],
    ['Sick Leave Days', summary.sickDays || 0],
    ['Local Leave Days', summary.localLeaveDays || 0],
    ['Public Holiday Days', summary.publicHolidayDays || 0],
    ['Absent Days', summary.absentDays || 0],
    ['Day Off Days', summary.dayOffDays || 0],
    ['Total Leave Hours', Number(summary.totalHours || 0).toFixed(2)]
  ];

  const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
  overviewSheet['!cols'] = [{ wch: 32 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

  const employeeBreakdownData = rows.map((row) => ({
    'Employee Name': row.employeeName,
    'Employee Code': row.employeeCode || '',
    'Department': row.department || 'Unassigned',
    'Sick Days': row.sickDays || 0,
    'Local Leave Days': row.localLeaveDays || 0,
    'Public Holiday Days': row.publicHolidayDays || 0,
    'Absent Days': row.absentDays || 0,
    'Day Off Days': row.dayOffDays || 0,
    'Total Leave Days': row.totalLeaveDays || 0,
    'Total Leave Hours': Number(row.totalLeaveHours || 0).toFixed(2)
  }));

  const breakdownSheet = XLSX.utils.json_to_sheet(employeeBreakdownData);
  breakdownSheet['!cols'] = [
    { wch: 24 },
    { wch: 16 },
    { wch: 18 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 }
  ];
  XLSX.utils.book_append_sheet(workbook, breakdownSheet, 'Employee Breakdown');

  const leaveEntriesData = records.map((record) => ({
    Date: moment(record.date).format('DD/MM/YYYY'),
    Employee: `${record.employees?.first_name || ''} ${record.employees?.last_name || ''}`.trim() || 'Unknown Employee',
    'Employee Code': record.employees?.employee_number || record.employees?.fingerprint_id || '',
    Department: record.employees?.department || 'Unassigned',
    'Leave Type': LEAVE_TYPE_LABELS[record.resolvedLeaveType] || record.resolvedLeaveType,
    'Total Hours': Number(record.parsedHours || 0).toFixed(2),
    Status: record.status
  }));

  const entriesSheet = XLSX.utils.json_to_sheet(leaveEntriesData);
  entriesSheet['!cols'] = [
    { wch: 12 },
    { wch: 24 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 12 },
    { wch: 10 }
  ];
  XLSX.utils.book_append_sheet(workbook, entriesSheet, 'Leave Entries');

  const safeType = leaveTypeFilter === 'all'
    ? 'all_types'
    : leaveTypeFilter.toLowerCase().replace(/\s+/g, '_');
  const filename = `Leave_Report_${safeType}_${moment(dateRange?.[0] || new Date()).format('YYYYMMDD')}_to_${moment(dateRange?.[1] || new Date()).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const formatAttendanceForExport = (attendance) => {
  return attendance.map(record => ({
    Date: moment(record.date).format('DD/MM/YYYY'),
    Employee: `${record.employees?.first_name || ''} ${record.employees?.last_name || ''}`.trim(),
    'Employee ID': record.employees?.fingerprint_id || record.employees?.employee_number || '',
    Department: record.employees?.department,
    'Check In': record.check_in,
    'Check Out': record.check_out,
    'Total Hours': Number(record.total_hours || 0).toFixed(2),
    Status: record.is_late ? 'Late' : record.is_early_out ? 'Early Out' : 'Regular',
    'Late Status': record.is_late ? 'Yes' : 'No',
    'Early Out Status': record.is_early_out ? 'Yes' : 'No',
    'Is Sunday': record.is_sunday ? 'Yes' : 'No',
    'Is Holiday': record.is_holiday ? 'Yes' : 'No',
    Notes: record.notes || ''
  }));
};

// Enhanced export functions
export const exportAttendanceReport = (attendance, dateRange, companyInfo = {}) => {
  const formattedData = formatAttendanceForExport(attendance);
  
  // Add summary data at the top
  const summaryData = [
    {
      'Summary Report': 'Attendance Report',
      'Company': companyInfo.name || 'Company Name',
      'Period': `${moment(dateRange[0]).format('DD/MM/YYYY')} - ${moment(dateRange[1]).format('DD/MM/YYYY')}`,
      'Generated': moment().format('DD/MM/YYYY HH:mm'),
      'Total Records': attendance.length
    },
    {}, // Empty row
    {
      'Total Employees': new Set(attendance.map(r => r.employee_id)).size,
      'Present Days': attendance.filter(r => r.status === 'present').length,
      'Late Entries': attendance.filter(r => r.is_late).length,
      'Early Departures': attendance.filter(r => r.is_early_out).length,
      'Total Hours': attendance.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0).toFixed(2)
    },
    {}, // Empty row
    {
      'Date': 'Date',
      'Employee': 'Employee Name',
      'Employee ID': 'Employee ID',
      'Department': 'Department',
      'Check In': 'Check In',
      'Check Out': 'Check Out',
      'Total Hours': 'Total Hours',
      'Status': 'Status',
      'Late Status': 'Late',
      'Early Out Status': 'Early Out',
      'Is Sunday': 'Sunday',
      'Is Holiday': 'Holiday',
      'Notes': 'Notes'
    },
    ...formattedData
  ];

  const worksheet = XLSX.utils.json_to_sheet(summaryData);
  
  // Set column widths
  const columnWidths = [
    { wch: 12 }, // Date
    { wch: 20 }, // Employee
    { wch: 12 }, // Employee ID
    { wch: 15 }, // Department
    { wch: 10 }, // Check In
    { wch: 10 }, // Check Out
    { wch: 12 }, // Total Hours
    { wch: 10 }, // Status
    { wch: 8 },  // Late
    { wch: 10 }, // Early Out
    { wch: 8 },  // Sunday
    { wch: 8 },  // Holiday
    { wch: 20 }  // Notes
  ];
  
  worksheet['!cols'] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');
  
  const filename = `Attendance_Report_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export employee summary
export const exportEmployeeSummary = (attendanceData, dateRange) => {
  // Group by employee
  const employeeGroups = attendanceData.reduce((acc, record) => {
    const empId = record.employee_id;
    if (!acc[empId]) {
      acc[empId] = {
        employee: record.employees,
        records: []
      };
    }
    acc[empId].records.push(record);
    return acc;
  }, {});

  // Calculate summary for each employee
  const summaryData = Object.values(employeeGroups).map(group => {
    const records = group.records;
    const totalHours = records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
    const presentDays = records.filter(r => r.status === 'present').length;
    const lateDays = records.filter(r => r.is_late).length;
    const earlyOutDays = records.filter(r => r.is_early_out).length;
    const regularDays = records.filter(r => !r.is_late && !r.is_early_out && r.status === 'present').length;

    return {
      'Employee Name': `${group.employee?.first_name || ''} ${group.employee?.last_name || ''}`.trim(),
      'Employee ID': group.employee?.fingerprint_id || group.employee?.employee_number || '',
      'Department': group.employee?.department || 'Unassigned',
      'Total Days': records.length,
      'Present Days': presentDays,
      'Absent Days': records.length - presentDays,
      'Regular Days': regularDays,
      'Late Days': lateDays,
      'Early Out Days': earlyOutDays,
      'Total Hours': totalHours.toFixed(2),
      'Average Hours': presentDays > 0 ? (totalHours / presentDays).toFixed(2) : '0.00',
      'Attendance Rate': presentDays > 0 ? ((presentDays / records.length) * 100).toFixed(2) + '%' : '0%'
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(summaryData);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 20 }, // Employee Name
    { wch: 12 }, // Employee ID
    { wch: 15 }, // Department
    { wch: 10 }, // Total Days
    { wch: 12 }, // Present Days
    { wch: 12 }, // Absent Days
    { wch: 12 }, // Regular Days
    { wch: 10 }, // Late Days
    { wch: 12 }, // Early Out Days
    { wch: 12 }, // Total Hours
    { wch: 12 }, // Average Hours
    { wch: 15 }  // Attendance Rate
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Summary');
  
  const filename = `Employee_Summary_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export raw timesheet entries
export const exportRawEntries = (rawEntries, dateRange) => {
  const formattedData = rawEntries.map(entry => ({
    'Entry ID': entry.id,
    'Employee ID': entry.employee_id,
    'Date Time': dayjs(entry.datetime).format('DD/MM/YYYY HH:mm:ss'),
    'Status': entry.status,
    'Action': entry.action === '01' ? 'Check In' : entry.action === '02' ? 'Check Out' : 'Unknown',
    'Device ID': entry.device_id,
    'Created At': dayjs(entry.created_at).format('DD/MM/YYYY HH:mm:ss')
  }));

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  
  worksheet['!cols'] = [
    { wch: 36 }, // Entry ID
    { wch: 36 }, // Employee ID
    { wch: 18 }, // Date Time
    { wch: 10 }, // Status
    { wch: 12 }, // Action
    { wch: 12 }, // Device ID
    { wch: 18 }  // Created At
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw Entries');
  
  const filename = `Raw_Entries_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export department-wise summary
export const exportDepartmentSummary = (attendanceData, dateRange) => {
  // Group by department
  const departmentGroups = attendanceData.reduce((acc, record) => {
    const dept = record.employees?.department || 'Unassigned';
    if (!acc[dept]) {
      acc[dept] = [];
    }
    acc[dept].push(record);
    return acc;
  }, {});

  // Calculate summary for each department
  const summaryData = Object.entries(departmentGroups).map(([department, records]) => {
    const employees = new Set(records.map(r => r.employee_id)).size;
    const totalHours = records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
    const presentDays = records.filter(r => r.status === 'present').length;
    const lateDays = records.filter(r => r.is_late).length;
    const earlyOutDays = records.filter(r => r.is_early_out).length;

    return {
      'Department': department,
      'Total Employees': employees,
      'Total Records': records.length,
      'Present Days': presentDays,
      'Absent Days': records.length - presentDays,
      'Late Entries': lateDays,
      'Early Departures': earlyOutDays,
      'Total Hours': totalHours.toFixed(2),
      'Average Hours per Employee': employees > 0 ? (totalHours / employees).toFixed(2) : '0.00',
      'Attendance Rate': records.length > 0 ? ((presentDays / records.length) * 100).toFixed(2) + '%' : '0%'
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(summaryData);
  
  worksheet['!cols'] = [
    { wch: 15 }, // Department
    { wch: 15 }, // Total Employees
    { wch: 12 }, // Total Records
    { wch: 12 }, // Present Days
    { wch: 12 }, // Absent Days
    { wch: 12 }, // Late Entries
    { wch: 15 }, // Early Departures
    { wch: 12 }, // Total Hours
    { wch: 20 }, // Average Hours per Employee
    { wch: 15 }  // Attendance Rate
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Department Summary');
  
  const filename = `Department_Summary_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export payroll-ready data
export const exportPayrollData = (attendanceData, dateRange, payrollSettings = {}) => {
  const {
    regularHoursPerDay = 8,
    overtimeRate = 1.5,
    weekendRate = 2.0,
    holidayRate = 2.5
  } = payrollSettings;

  // Group by employee
  const employeeGroups = attendanceData.reduce((acc, record) => {
    const empId = record.employee_id;
    if (!acc[empId]) {
      acc[empId] = {
        employee: record.employees,
        records: []
      };
    }
    acc[empId].records.push(record);
    return acc;
  }, {});

  // Calculate payroll data for each employee
  const payrollData = Object.values(employeeGroups).map(group => {
    const records = group.records;
    let regularHours = 0;
    let overtimeHours = 0;
    let weekendHours = 0;
    let holidayHours = 0;

    records.forEach(record => {
      const hours = parseFloat(record.total_hours) || 0;
      
      if (record.is_holiday) {
        holidayHours += hours;
      } else if (record.is_sunday) {
        weekendHours += hours;
      } else {
        if (hours <= regularHoursPerDay) {
          regularHours += hours;
        } else {
          regularHours += regularHoursPerDay;
          overtimeHours += (hours - regularHoursPerDay);
        }
      }
    });

    const totalHours = regularHours + overtimeHours + weekendHours + holidayHours;
    const presentDays = records.filter(r => r.status === 'present').length;
    const lateDays = records.filter(r => r.is_late).length;
    const hourlyRate = group.employee?.hourly_rate || 0;

    return {
      'Employee Name': `${group.employee?.first_name || ''} ${group.employee?.last_name || ''}`.trim(),
      'Employee ID': group.employee?.fingerprint_id || group.employee?.employee_number || '',
      'Department': group.employee?.department || 'Unassigned',
      'Present Days': presentDays,
      'Absent Days': records.length - presentDays,
      'Late Days': lateDays,
      'Regular Hours': regularHours.toFixed(2),
      'Overtime Hours': overtimeHours.toFixed(2),
      'Weekend Hours': weekendHours.toFixed(2),
      'Holiday Hours': holidayHours.toFixed(2),
      'Total Hours': totalHours.toFixed(2),
      'Hourly Rate': hourlyRate,
      'Regular Pay': (hourlyRate * regularHours).toFixed(2),
      'Overtime Pay': (hourlyRate * overtimeHours * overtimeRate).toFixed(2),
      'Weekend Pay': (hourlyRate * weekendHours * weekendRate).toFixed(2),
      'Holiday Pay': (hourlyRate * holidayHours * holidayRate).toFixed(2),
      'Total Pay': (
        (hourlyRate * regularHours) +
        (hourlyRate * overtimeHours * overtimeRate) +
        (hourlyRate * weekendHours * weekendRate) +
        (hourlyRate * holidayHours * holidayRate)
      ).toFixed(2)
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(payrollData);
  
  worksheet['!cols'] = [
    { wch: 20 }, // Employee Name
    { wch: 12 }, // Employee ID
    { wch: 15 }, // Department
    { wch: 12 }, // Present Days
    { wch: 12 }, // Absent Days
    { wch: 10 }, // Late Days
    { wch: 12 }, // Regular Hours
    { wch: 12 }, // Overtime Hours
    { wch: 12 }, // Weekend Hours
    { wch: 12 }, // Holiday Hours
    { wch: 12 }, // Total Hours
    { wch: 12 }, // Hourly Rate
    { wch: 12 }, // Regular Pay
    { wch: 12 }, // Overtime Pay
    { wch: 12 }, // Weekend Pay
    { wch: 12 }, // Holiday Pay
    { wch: 12 }  // Total Pay
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll Data');
  
  const filename = `Payroll_Data_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export multiple sheets in one workbook
export const exportComprehensiveReport = (attendanceData, rawEntries, dateRange, companyInfo = {}) => {
  const workbook = XLSX.utils.book_new();

  // 1. Summary Sheet
  const summaryData = [{
    'Report Type': 'Comprehensive Attendance Report',
    'Company': companyInfo.name || 'Company Name',
    'Period': `${moment(dateRange[0]).format('DD/MM/YYYY')} - ${moment(dateRange[1]).format('DD/MM/YYYY')}`,
    'Generated': moment().format('DD/MM/YYYY HH:mm:ss'),
    'Total Records': attendanceData.length,
    'Total Employees': new Set(attendanceData.map(r => r.employee_id)).size,
    'Total Raw Entries': rawEntries?.length || 0
  }];
  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // 2. Attendance Data Sheet
  const attendanceSheet = XLSX.utils.json_to_sheet(formatAttendanceForExport(attendanceData));
  XLSX.utils.book_append_sheet(workbook, attendanceSheet, 'Attendance');

  // 3. Employee Summary Sheet
  const employeeGroups = attendanceData.reduce((acc, record) => {
    const empId = record.employee_id;
    if (!acc[empId]) {
      acc[empId] = { employee: record.employees, records: [] };
    }
    acc[empId].records.push(record);
    return acc;
  }, {});

  const employeeSummaryData = Object.values(employeeGroups).map(group => {
    const records = group.records;
    const totalHours = records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
    const presentDays = records.filter(r => r.status === 'present').length;

    return {
      'Employee Name': `${group.employee?.first_name || ''} ${group.employee?.last_name || ''}`.trim(),
      'Employee ID': group.employee?.fingerprint_id || '',
      'Department': group.employee?.department || 'Unassigned',
      'Total Days': records.length,
      'Present Days': presentDays,
      'Late Days': records.filter(r => r.is_late).length,
      'Total Hours': totalHours.toFixed(2),
      'Average Hours': presentDays > 0 ? (totalHours / presentDays).toFixed(2) : '0.00'
    };
  });
  const employeeSheet = XLSX.utils.json_to_sheet(employeeSummaryData);
  XLSX.utils.book_append_sheet(workbook, employeeSheet, 'Employee Summary');

  // 4. Department Summary Sheet
  const departmentGroups = attendanceData.reduce((acc, record) => {
    const dept = record.employees?.department || 'Unassigned';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(record);
    return acc;
  }, {});

  const departmentSummaryData = Object.entries(departmentGroups).map(([department, records]) => ({
    'Department': department,
    'Total Employees': new Set(records.map(r => r.employee_id)).size,
    'Total Records': records.length,
    'Present Days': records.filter(r => r.status === 'present').length,
    'Late Entries': records.filter(r => r.is_late).length,
    'Total Hours': records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0).toFixed(2)
  }));
  const departmentSheet = XLSX.utils.json_to_sheet(departmentSummaryData);
  XLSX.utils.book_append_sheet(workbook, departmentSheet, 'Department Summary');

  // 5. Raw Entries Sheet (if provided)
  if (rawEntries && rawEntries.length > 0) {
    const rawEntriesData = rawEntries.map(entry => ({
      'Entry ID': entry.id,
      'Employee ID': entry.employee_id,
      'Date Time': dayjs(entry.datetime).format('DD/MM/YYYY HH:mm:ss'),
      'Status': entry.status,
      'Action': entry.action === '01' ? 'Check In' : 'Check Out',
      'Device ID': entry.device_id
    }));
    const rawSheet = XLSX.utils.json_to_sheet(rawEntriesData);
    XLSX.utils.book_append_sheet(workbook, rawSheet, 'Raw Entries');
  }

  const filename = `Comprehensive_Report_${moment(dateRange[0]).format('YYYYMMDD')}_to_${moment(dateRange[1]).format('YYYYMMDD')}`;
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Utility function to download CSV
export const downloadCSV = (data, filename) => {
  const csvContent = convertToCSV(data);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Convert array of objects to CSV string
const convertToCSV = (data) => {
  if (!data || data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(',');
  
  const csvRows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Escape quotes and wrap in quotes if necessary
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',')
  );
  
  return [csvHeaders, ...csvRows].join('\n');
};

// Default export for backward compatibility
export default {
  exportToExcel,
  formatAttendanceForExport,
  exportAttendanceReport,
  exportEmployeeSummary,
  exportRawEntries,
  exportDepartmentSummary,
  exportPayrollData,
  exportComprehensiveReport,
  exportLeaveTypeReport,
  downloadCSV
};
